import { IconCheck } from "@tabler/icons-react";
import type { ComponentType } from "react";
import type { IconProps } from "@tabler/icons-react";
import { cn } from "@/lib/cn";

export interface StepperStep {
  key: string;
  label: string;
  description?: string;
  Icon: ComponentType<IconProps>;
}

export interface VerticalStepperProps {
  steps: StepperStep[];
  /** Paso que el usuario está VIENDO en pantalla · recibe el halo/pulso fuerte. */
  activeIndex: number;
  /** Paso más avanzado al que la solicitud ya llegó (completados = anteriores a este). */
  maxAccesibleIndex: number;
  isClickable: (idx: number) => boolean;
  onStepClick?: (idx: number) => void;
}

/**
 * Stepper vertical genérico · reemplaza `<Stepper orientation="vertical">`
 * de Mantine. Sin equivalente drop-in en Tailwind, así que esto es
 * construcción nueva (no un "port 1:1"): círculo de ícono + línea
 * conectora + label/descripción, con el estado (completado/progreso/
 * pendiente) y el halo del paso "visitado" resueltos como clases
 * condicionales de React — no como el hack CSS `:nth-child(N)` del
 * original (`highlight-styles.ts`), que ya no existe.
 */
export function VerticalStepper({ steps, activeIndex, maxAccesibleIndex, isClickable, onStepClick }: VerticalStepperProps) {
  return (
    <ol className="flex flex-col">
      {steps.map((step, idx) => {
        const completado = idx < maxAccesibleIndex;
        const visitado = idx === activeIndex;
        // El halo fuerte es del paso VISITADO · si además es el de progreso
        // real pero el usuario navegó a otro paso, se muestra neutral (igual
        // que el original: "activeIndex !== maxAccesibleIndex" apaga el highlight).
        const resaltado = visitado;
        const clickable = isClickable(idx);
        const esUltimo = idx === steps.length - 1;
        const Icon = step.Icon;

        return (
          <li key={step.key} className="relative flex gap-3 pb-6 last:pb-0">
            {!esUltimo && (
              <span
                className={cn("absolute left-[15px] top-8 w-px", completado ? "bg-multiva-600" : "bg-border")}
                style={{ height: "calc(100% - 32px)" }}
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(idx)}
              aria-label={`${step.label}${step.description ? ` · ${step.description}` : ""}`}
              aria-current={visitado ? "step" : undefined}
              className={cn("relative z-[1] flex size-[30px] shrink-0 items-center justify-center rounded-full border-2 transition-all duration-150", clickable ? "cursor-pointer" : "cursor-default")}
              style={
                completado || resaltado
                  ? { backgroundColor: "var(--color-multiva-600)", borderColor: "var(--color-multiva-600)", color: "#fff" }
                  : undefined
              }
            >
              {resaltado && (
                <span
                  className="absolute -inset-1.5 animate-halo-pulse rounded-full border-[3px]"
                  style={{ borderColor: "var(--color-multiva-300)" }}
                  aria-hidden="true"
                />
              )}
              <span
                className={cn(
                  "flex items-center justify-center",
                  !(completado || resaltado) && "text-muted-foreground"
                )}
              >
                {completado ? <IconCheck size={16} stroke={2} /> : <Icon size={16} stroke={resaltado ? 2 : 1.7} />}
              </span>
            </button>

            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(idx)}
              className={cn("min-w-0 flex-1 pt-1 text-left", clickable ? "cursor-pointer" : "cursor-default")}
            >
              <p
                className={cn(
                  "text-[13px] leading-tight",
                  resaltado ? "font-bold text-multiva-700" : completado ? "font-medium text-foreground" : "font-medium text-muted-foreground"
                )}
              >
                {step.label}
              </p>
              {step.description && (
                <p className={cn("mt-0.5 text-[11px] leading-tight", resaltado ? "font-medium text-multiva-600" : "text-muted-foreground")}>
                  {step.description}
                </p>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
